import { Component, OnInit, inject, signal } from '@angular/core';
import { Router, RouterLink } from '@angular/router';
import { FormsModule } from '@angular/forms';
import { MessageService } from 'primeng/api';
import { ButtonModule } from 'primeng/button';
import { DialogModule } from 'primeng/dialog';
import { InputNumberModule } from 'primeng/inputnumber';
import { InputTextModule } from 'primeng/inputtext';
import { SelectModule } from 'primeng/select';
import { TableModule } from 'primeng/table';
import { TabsModule } from 'primeng/tabs';
import { TextareaModule } from 'primeng/textarea';
import { LocalizationService } from '../../core/localization/localization.service';
import { AinFormFieldComponent } from '../../shared/design-system/form-field.component';
import { InfoHintComponent } from '../../shared/design-system/info-hint.component';
import { AinPageComponent } from '../../shared/design-system/page.component';
import { AinDatePipe } from '../../shared/design-system/ain-date.pipe';
import { AinStateComponent } from '../../shared/design-system/state.component';
import { GrowthContentAssetSelectorComponent } from '../../shared/design-system/growth-content-asset-selector.component';
import { GrowthSegmentSelectorComponent } from '../../shared/design-system/growth-segment-selector.component';
import { KnowledgeSourceSelectorComponent } from '../../shared/design-system/knowledge-source-selector.component';
import { OfferingSelectorComponent } from '../../shared/design-system/offering-selector.component';
import {
  GrowthContentBrief,
  GrowthContentAsset,
  GrowthContentInsightArtifact,
  GrowthContentPlanListItem,
  GrowthContentProductionRun,
  GrowthContentTemplatePack,
  GrowthMarketingService,
  GrowthPublishPlan,
  GrowthRenderJob
} from './growth-marketing.service';
import { buildGrowthMarketingChannelOptions } from './growth-channel.constants';

interface BriefChannelTargetRow {
  channelKey: string;
  priority: number;
}

interface BriefSourceBindingRow {
  bindingType: string;
  bindingRef: string;
}

@Component({
  standalone: true,
  selector: 'ain-growth-ace-content-studio',
  providers: [MessageService],
  imports: [
    AinPageComponent,
    AinStateComponent,
    AinFormFieldComponent,
    InfoHintComponent,
    FormsModule,
    TableModule,
    ButtonModule,
    DialogModule,
    SelectModule,
    InputTextModule,
    InputNumberModule,
    TextareaModule,
    TabsModule,
    GrowthSegmentSelectorComponent,
    GrowthContentAssetSelectorComponent,
    KnowledgeSourceSelectorComponent,
    OfferingSelectorComponent,
    AinDatePipe,
    RouterLink
  ],
  template: `
    <ain-page
      [eyebrow]="t('growth.eyebrow')"
      [title]="t('growth.content.ace.studio.title')"
      [description]="t('growth.content.ace.studio.description')">
      <ain-info-hint
        page-title-addon
        [titleKey]="'growth.content.ace.studio.hint.title'"
        [descriptionKey]="'growth.content.ace.studio.hint.description'"
        [bulletKeys]="[
          'growth.content.ace.studio.hint.bullets.draftOnly',
          'growth.content.ace.studio.hint.bullets.approvalGate',
          'growth.content.ace.studio.hint.bullets.templateCertified'
        ]" />

      <p-tabs value="briefs">
        <p-tablist>
          <p-tab value="briefs">{{ t('growth.content.ace.tabs.briefs') }}</p-tab>
          <p-tab value="runs">{{ t('growth.content.ace.tabs.productionRuns') }}</p-tab>
          <p-tab value="templates">{{ t('growth.content.ace.tabs.templates') }}</p-tab>
          <p-tab value="render">{{ t('growth.content.ace.tabs.renderPublish') }}</p-tab>
          <p-tab value="insights">{{ t('growth.content.ace.tabs.insights') }}</p-tab>
        </p-tablist>
        <p-tabpanels>
          <p-tabpanel value="briefs">
            <section class="card">
              <div class="toolbar">
                <a pButton [routerLink]="['/growth/marketing/ace-studio/briefs/new']" [label]="t('growth.content.ace.actions.newBrief')"></a>
                <p-select
                  [options]="contentPlanOptions()"
                  optionLabel="label"
                  optionValue="value"
                  [(ngModel)]="selectedContentPlanId"
                  [showClear]="true"
                  [placeholder]="t('growth.content.ace.filters.allCalendars')"
                  appendTo="body"
                  (ngModelChange)="load()" />
                <button pButton type="button" [label]="t('growth.content.ace.actions.approveSelected')" [disabled]="selectedBriefs().length === 0" (click)="approveSelected()"></button>
                <button pButton type="button" severity="secondary" [label]="t('growth.content.ace.actions.produceSelected')" [disabled]="selectedBriefs().length === 0" (click)="produceSelected()"></button>
                <button pButton type="button" severity="secondary" [label]="t('common.refresh')" (click)="load()"></button>
                <a routerLink="/growth/marketing/article-publishing" pButton severity="help" [label]="t('growth.marketing.calendar.actions.openApprovalQueue')"></a>
              </div>
              @if (loading()) {
                <ain-state type="loading" [title]="t('common.loading')" />
              } @else if (briefs().length === 0) {
                <ain-state type="empty" [title]="t('growth.content.ace.empty.briefs')" />
              } @else {
                <p-table [value]="briefs()" [rows]="10" [paginator]="true" dataKey="id" [(selection)]="selectedBriefsModel">
                  <ng-template pTemplate="header">
                    <tr>
                      <th style="width:3rem"><p-tableHeaderCheckbox /></th>
                      <th>{{ t('growth.content.ace.fields.contentType') }}</th>
                      <th>{{ t('growth.content.ace.fields.goal') }}</th>
                      <th>{{ t('growth.content.ace.fields.targetAudience') }}</th>
                      <th>{{ t('growth.content.ace.fields.calendar') }}</th>
                      <th>{{ t('growth.content.ace.fields.status') }}</th>
                      <th>{{ t('growth.content.ace.fields.actions') }}</th>
                    </tr>
                  </ng-template>
                  <ng-template pTemplate="body" let-row>
                    <tr>
                      <td><p-tableCheckbox [value]="row" /></td>
                      <td>{{ row.contentType }}</td>
                      <td>{{ row.goal }}</td>
                      <td>{{ row.targetAudience }}</td>
                      <td>{{ row.contentPlanName || '—' }}</td>
                      <td>{{ briefStatusLabel(row.status) }}</td>
                      <td class="actions">
                        <a pButton size="small" severity="secondary" [routerLink]="['/growth/marketing/ace-studio/briefs', row.id]" [label]="t('common.edit')"></a>
                        <button pButton type="button" size="small" [label]="t('growth.content.ace.actions.produce')" (click)="produce(row)"></button>
                        <button pButton type="button" size="small" severity="help" [label]="t('growth.marketing.calendar.actions.openApprovalQueue')" (click)="openArticlePublishing(row)"></button>
                      </td>
                    </tr>
                  </ng-template>
                </p-table>
              }
            </section>
          </p-tabpanel>

          <p-tabpanel value="runs">
            <section class="card">
              <p-table [value]="productionRuns()" [rows]="10" [paginator]="true">
                <ng-template pTemplate="header">
                  <tr>
                    <th>{{ t('growth.content.ace.fields.runId') }}</th>
                    <th>{{ t('growth.content.ace.fields.status') }}</th>
                    <th>{{ t('growth.content.ace.fields.startedAt') }}</th>
                    <th>{{ t('growth.content.ace.fields.completedAt') }}</th>
                    <th>{{ t('growth.content.ace.fields.actions') }}</th>
                  </tr>
                </ng-template>
                <ng-template pTemplate="body" let-row>
                  <tr>
                    <td>{{ row.id }}</td>
                    <td>{{ row.status }}</td>
                    <td>{{ row.startedAtUtc | ainDate:'datetime' }}</td>
                    <td>{{ row.completedAtUtc ? (row.completedAtUtc | ainDate:'datetime') : '-' }}</td>
                    <td><button pButton type="button" size="small" severity="danger" [label]="t('growth.content.ace.actions.cancelRun')" (click)="cancelRun(row)"></button></td>
                  </tr>
                </ng-template>
              </p-table>
            </section>
          </p-tabpanel>

          <p-tabpanel value="templates">
            <section class="card">
              <p-table [value]="templatePacks()" [rows]="10" [paginator]="true">
                <ng-template pTemplate="header">
                  <tr>
                    <th>{{ t('growth.content.ace.fields.templateKey') }}</th>
                    <th>{{ t('growth.content.ace.fields.ownerScope') }}</th>
                    <th>{{ t('growth.content.ace.fields.contentType') }}</th>
                    <th>{{ t('growth.content.ace.fields.version') }}</th>
                    <th>{{ t('growth.content.ace.fields.certified') }}</th>
                  </tr>
                </ng-template>
                <ng-template pTemplate="body" let-row>
                  <tr>
                    <td>{{ row.key }}</td>
                    <td>{{ row.ownerScope }}</td>
                    <td>{{ row.contentType }}</td>
                    <td>{{ row.version }}</td>
                    <td>{{ row.isCertified }}</td>
                  </tr>
                </ng-template>
              </p-table>
            </section>
          </p-tabpanel>

          <p-tabpanel value="render">
            <section class="card">
              <div class="grid">
                <ain-form-field [label]="t('growth.content.ace.fields.contentForRender')">
                  <p-select [options]="producedContentOptions()" optionLabel="label" optionValue="value" [(ngModel)]="selectedContentIdForRender" appendTo="body" [showClear]="true" />
                </ain-form-field>
                <ain-form-field [label]="t('growth.content.ace.fields.renderFormat')">
                  <p-select [options]="renderFormatOptions" optionLabel="label" optionValue="value" [(ngModel)]="renderFormat" appendTo="body" />
                </ain-form-field>
              </div>
              <div class="toolbar">
                <button pButton type="button" [label]="t('growth.content.ace.actions.render')" (click)="renderContent()"></button>
                <button pButton type="button" severity="secondary" [label]="t('growth.content.ace.actions.suggestPublishPlan')" (click)="suggestPublishPlan()"></button>
              </div>
              <p-table [value]="renderJobs()">
                <ng-template pTemplate="header"><tr><th>Job</th><th>Format</th><th>Status</th><th>Artifact</th></tr></ng-template>
                <ng-template pTemplate="body" let-row><tr><td>{{ row.id }}</td><td>{{ row.format }}</td><td>{{ row.status }}</td><td>{{ row.artifactRef }}</td></tr></ng-template>
              </p-table>
              <p-table [value]="publishPlans()">
                <ng-template pTemplate="header"><tr><th>Plan</th><th>Status</th><th>Consent</th><th>Channels</th></tr></ng-template>
                <ng-template pTemplate="body" let-row><tr><td>{{ row.id }}</td><td>{{ row.status }}</td><td>{{ row.consentScope }}</td><td>{{ row.channelsJson }}</td></tr></ng-template>
              </p-table>
            </section>
          </p-tabpanel>

          <p-tabpanel value="insights">
            <section class="card">
              <div class="toolbar">
                <ain-form-field [label]="t('growth.content.ace.fields.insightSegment')">
                  <ain-growth-segment-selector [(ngModel)]="insightSegmentId" />
                </ain-form-field>
                <button pButton type="button" [label]="t('growth.content.ace.actions.createInsight')" (click)="createInsight()"></button>
                <button pButton type="button" severity="secondary" [label]="t('growth.content.ace.actions.createBriefFromInsight')" [disabled]="!latestInsightId" (click)="createBriefFromInsight()"></button>
              </div>
              <p-table [value]="insights()">
                <ng-template pTemplate="header"><tr><th>Insight</th><th>Scope</th><th>Period</th><th>Recommendations</th></tr></ng-template>
                <ng-template pTemplate="body" let-row><tr><td>{{ row.id }}</td><td>{{ row.scope }}</td><td>{{ row.period }}</td><td>{{ row.recommendationsJson }}</td></tr></ng-template>
              </p-table>
            </section>
          </p-tabpanel>
        </p-tabpanels>
      </p-tabs>

      <p-dialog [header]="t('growth.content.ace.dialogs.brief')" [visible]="briefDialogVisible()" (visibleChange)="briefDialogVisible.set($event)" [modal]="true" [style]="{ width: 'min(880px,96vw)' }">
        <div class="grid">
          <ain-form-field [label]="t('growth.content.ace.fields.ownerScope')">
            <p-select [options]="ownerScopeOptions" optionLabel="label" optionValue="value" [(ngModel)]="briefModel.ownerScope" appendTo="body" />
          </ain-form-field>
          <ain-form-field [label]="t('growth.content.ace.fields.contentType')">
            <p-select [options]="contentTypeOptions" optionLabel="label" optionValue="value" [(ngModel)]="briefModel.contentType" appendTo="body" />
          </ain-form-field>
          <ain-form-field [label]="t('growth.content.ace.fields.targetAudience')"><input pInputText [(ngModel)]="briefModel.targetAudience" /></ain-form-field>
          <ain-form-field [label]="t('growth.content.ace.fields.goal')"><input pInputText [(ngModel)]="briefModel.goal" /></ain-form-field>
          <ain-form-field [label]="t('growth.content.ace.fields.locale')"><input pInputText [(ngModel)]="briefModel.locale" /></ain-form-field>
          <ain-form-field [label]="t('growth.content.ace.fields.templatePack')">
            <p-select [options]="templatePackOptions()" optionLabel="label" optionValue="value" [(ngModel)]="briefModel.templatePackKey" appendTo="body" [showClear]="true" />
          </ain-form-field>
        </div>

        <div class="section-title"><h4>{{ t('growth.content.ace.sections.channelTargets') }}</h4></div>
        @for (row of channelTargets; track $index) {
          <div class="row-grid">
            <ain-form-field [label]="t('growth.content.ace.fields.channel')">
              <p-select [options]="channelOptions()" optionLabel="label" optionValue="value" [(ngModel)]="row.channelKey" appendTo="body" />
            </ain-form-field>
            <ain-form-field [label]="t('growth.content.ace.fields.priority')"><p-inputNumber [(ngModel)]="row.priority" [min]="1" /></ain-form-field>
            <button pButton type="button" severity="danger" icon="pi pi-trash" (click)="removeChannelTarget($index)"></button>
          </div>
        }
        <button pButton type="button" severity="secondary" [label]="t('common.add')" (click)="addChannelTarget()"></button>

        <div class="section-title">
          <h4>{{ t('growth.content.ace.sections.sourceBindings') }}</h4>
          <ain-info-hint
            [titleKey]="'growth.content.ace.help.sourceBindings.title'"
            [descriptionKey]="'growth.content.ace.help.sourceBindings.description'" />
        </div>
        @for (row of sourceBindings; track $index) {
          <div class="row-grid">
            <ain-form-field [label]="t('growth.content.ace.fields.bindingType')">
              <p-select [options]="bindingTypeOptions" optionLabel="label" optionValue="value" [(ngModel)]="row.bindingType" appendTo="body" />
            </ain-form-field>
            <ain-form-field [label]="t('growth.content.ace.fields.bindingRef')">
              @if (row.bindingType === 'offering' || row.bindingType === 'catalog') {
                <ain-offering-selector [(ngModel)]="row.bindingRef" />
              } @else if (row.bindingType === 'segment') {
                <ain-growth-segment-selector [(ngModel)]="row.bindingRef" />
              } @else if (row.bindingType === 'content-asset') {
                <ain-growth-content-asset-selector [(ngModel)]="row.bindingRef" />
              } @else if (row.bindingType === 'knowledge') {
                <ain-knowledge-source-selector [(ngModel)]="row.bindingRef" />
              }
            </ain-form-field>
            <button pButton type="button" severity="danger" icon="pi pi-trash" (click)="removeSourceBinding($index)"></button>
          </div>
        }
        <button pButton type="button" severity="secondary" [label]="t('common.add')" (click)="addSourceBinding()"></button>

        <ng-template pTemplate="footer">
          <button pButton type="button" severity="secondary" [label]="t('common.cancel')" (click)="briefDialogVisible.set(false)"></button>
          <button pButton type="button" [label]="t('common.save')" (click)="saveBrief()"></button>
        </ng-template>
      </p-dialog>
    </ain-page>
  `,
  styles: [`
    .card { border: 1px solid #e5e7eb; border-radius: 12px; background: #fff; padding: 12px; margin-bottom: 12px; display: grid; gap: 10px; }
    .toolbar { display: flex; gap: 8px; flex-wrap: wrap; }
    .grid { display: grid; gap: 12px; grid-template-columns: repeat(auto-fit, minmax(220px, 1fr)); }
    .section-title { display: flex; align-items: center; gap: 8px; margin-top: 8px; }
    .row-grid { display: grid; gap: 8px; grid-template-columns: repeat(auto-fit, minmax(150px, 1fr)); align-items: end; margin-bottom: 8px; }
    .actions { display: flex; gap: 6px; flex-wrap: wrap; }
  `]
})
export class GrowthAceContentStudioComponent implements OnInit {
  private readonly service = inject(GrowthMarketingService);
  private readonly i18n = inject(LocalizationService);
  private readonly router = inject(Router);
  private readonly messages = inject(MessageService);

  readonly loading = signal(false);
  readonly briefs = signal<GrowthContentBrief[]>([]);
  readonly contentPlans = signal<GrowthContentPlanListItem[]>([]);
  readonly productionRuns = signal<GrowthContentProductionRun[]>([]);
  readonly templatePacks = signal<GrowthContentTemplatePack[]>([]);
  readonly renderJobs = signal<GrowthRenderJob[]>([]);
  readonly publishPlans = signal<GrowthPublishPlan[]>([]);
  readonly insights = signal<GrowthContentInsightArtifact[]>([]);
  readonly producedAssets = signal<GrowthContentAsset[]>([]);
  readonly briefDialogVisible = signal(false);

  selectedBriefId: string | null = null;
  selectedContentPlanId: string | null = null;
  selectedContentIdForRender: string | null = null;
  selectedBriefsModel: GrowthContentBrief[] = [];
  latestInsightId: string | null = null;
  insightSegmentId: string | null = null;
  renderFormat = 'html';

  briefModel = {
    ownerScope: 'tenant',
    contentType: 'article',
    targetAudience: '',
    goal: '',
    locale: 'fa-IR',
    templatePackKey: ''
  };

  channelTargets: BriefChannelTargetRow[] = [{ channelKey: 'web-blog', priority: 1 }];
  sourceBindings: BriefSourceBindingRow[] = [{ bindingType: 'catalog', bindingRef: '' }];

  readonly ownerScopeOptions = [{ value: 'tenant', label: 'Tenant' }, { value: 'platform', label: 'Platform' }];
  readonly contentTypeOptions = [
    { value: 'article', label: 'Article' },
    { value: 'landing-page', label: 'Landing Page' },
    { value: 'social-post', label: 'Social post' },
    { value: 'brochure', label: 'Brochure' },
    { value: 'pitch-deck', label: 'Pitch Deck' },
    { value: 'product-catalog', label: 'Product Catalog' },
    { value: 'company-profile', label: 'Company Profile' },
    { value: 'video-script', label: 'Video Script' },
    { value: 'demo-storyboard', label: 'Demo Storyboard' }
  ];
  channelOptions(): Array<{ label: string; value: string }> {
    return buildGrowthMarketingChannelOptions(key => this.t(key));
  }

  readonly bindingTypeOptions = [
    { value: 'catalog', label: 'Catalog / Offering' },
    { value: 'offering', label: 'Offering' },
    { value: 'segment', label: 'Segment' },
    { value: 'content-asset', label: 'Content asset' },
    { value: 'knowledge', label: 'Knowledge corpus' }
  ];
  readonly renderFormatOptions = [
    { value: 'html', label: 'HTML Preview' },
    { value: 'markdown-preview', label: 'Markdown Preview' },
    { value: 'pdf', label: 'PDF' },
    { value: 'pptx', label: 'PPTX' }
  ];

  ngOnInit(): void {
    this.load();
  }

  load(): void {
    this.loading.set(true);
    this.selectedBriefsModel = [];
    this.service.listContentPlans().subscribe({ next: items => this.contentPlans.set(items) });
    this.service.listContentBriefs(this.selectedContentPlanId ?? undefined).subscribe({ next: items => this.briefs.set(items) });
    this.service.listContentTemplatePacks().subscribe({ next: items => this.templatePacks.set(items) });
    this.service.listContent().subscribe({
      next: items => {
        this.producedAssets.set(items);
        this.productionRuns.set([]);
        this.publishPlans.set([]);
        this.renderJobs.set([]);
        if (!this.selectedContentIdForRender && items.length > 0)
          this.selectedContentIdForRender = items[0].id;
        this.loading.set(false);
      },
      error: () => this.loading.set(false)
    });
  }

  contentPlanOptions(): Array<{ label: string; value: string }> {
    return this.contentPlans().map(plan => ({
      label: `${plan.name} (${plan.itemCount})`,
      value: plan.id
    }));
  }

  selectedBriefs(): GrowthContentBrief[] {
    return this.selectedBriefsModel;
  }

  briefStatusLabel(status: string | number): string {
    const key = this.resolveBriefStatusKey(status);
    return key ? this.t(key) : String(status);
  }

  approveSelected(): void {
    const ids = this.selectedBriefsModel.map(brief => brief.id);
    if (ids.length === 0)
      return;
    this.service.approveBriefs(ids).subscribe({
      next: result => {
        this.messages.add({
          severity: 'success',
          summary: `${this.t('growth.content.ace.toasts.briefsApproved')} (${result.approved})`
        });
        this.load();
      }
    });
  }

  produceSelected(): void {
    const ids = this.selectedBriefsModel.map(brief => brief.id);
    if (ids.length === 0)
      return;
    this.service.produceBriefs(ids).subscribe({
      next: result => {
        this.messages.add({
          severity: 'success',
          summary: `${this.t('growth.content.ace.toasts.briefsProduced')} (${result.produced})`
        });
        if (result.skipped.length > 0) {
          this.messages.add({
            severity: 'warn',
            summary: `${this.t('growth.content.ace.toasts.briefsSkipped')} (${result.skipped.length})`
          });
        }
        this.load();
      }
    });
  }

  templatePackOptions() {
    return this.templatePacks()
      .filter(item => item.contentType === this.briefModel.contentType && item.ownerScope === this.briefModel.ownerScope && item.isActive && item.isCertified)
      .map(item => ({ value: item.key, label: `${item.key} (${item.version})` }));
  }

  producedContentOptions() {
    return this.producedAssets().map(item => ({ value: item.id, label: `${item.title} (${item.contentType})` }));
  }

  openBriefDialog(): void {
    this.selectedBriefId = null;
    this.briefModel = { ownerScope: 'tenant', contentType: 'article', targetAudience: '', goal: '', locale: 'fa-IR', templatePackKey: '' };
    this.channelTargets = [{ channelKey: 'web-blog', priority: 1 }];
    this.sourceBindings = [{ bindingType: 'catalog', bindingRef: '' }];
    this.briefDialogVisible.set(true);
  }

  editBrief(brief: GrowthContentBrief): void {
    this.selectedBriefId = brief.id;
    this.briefModel = {
      ownerScope: brief.ownerScope,
      contentType: brief.contentType,
      targetAudience: brief.targetAudience,
      goal: brief.goal,
      locale: brief.locale,
      templatePackKey: ''
    };
    this.channelTargets = this.parseJson<BriefChannelTargetRow[]>(brief.channelTargetsJson, [{ channelKey: 'web-blog', priority: 1 }]);
    this.sourceBindings = this.parseJson<BriefSourceBindingRow[]>(brief.sourceBindingsJson, [{ bindingType: 'catalog', bindingRef: '' }]);
    this.briefDialogVisible.set(true);
  }

  saveBrief(): void {
    const payload = {
      id: this.selectedBriefId,
      ownerScope: this.briefModel.ownerScope,
      contentType: this.briefModel.contentType,
      targetAudience: this.briefModel.targetAudience,
      goal: this.briefModel.goal,
      locale: this.briefModel.locale,
      channelTargetsJson: JSON.stringify(this.channelTargets),
      sourceBindingsJson: JSON.stringify(this.sourceBindings),
      correlationId: null,
      idempotencyKey: this.selectedBriefId ? `brief-update-${this.selectedBriefId}` : `brief-create-${Date.now()}`
    };
    const request$ = this.selectedBriefId
      ? this.service.updateContentBrief(this.selectedBriefId, payload)
      : this.service.createContentBrief(payload);
    request$.subscribe({
      next: () => {
        this.briefDialogVisible.set(false);
        this.load();
      }
    });
  }

  produce(brief: GrowthContentBrief): void {
    const selectedTemplate = this.templatePacks()
      .find(item => item.contentType === brief.contentType && item.ownerScope === brief.ownerScope && item.isActive && item.isCertified);
    if (!selectedTemplate)
      return;
    this.service.produceContentBrief(brief.id, {
      templatePackKey: selectedTemplate.key,
      idempotencyKey: `produce-${brief.id}-${Date.now()}`
    }).subscribe({
      next: result => {
        this.productionRuns.set([result.run, ...this.productionRuns()]);
        this.selectedContentIdForRender = result.output.id;
        this.load();
        this.router.navigate(['/growth/marketing/article-publishing', result.output.id], {
          queryParams: { briefId: brief.id }
        });
      }
    });
  }

  cancelRun(run: GrowthContentProductionRun): void {
    this.service.cancelContentProductionRun(run.id, 'cancelled-from-admin').subscribe({
      next: cancelled => {
        this.productionRuns.set(this.productionRuns().map(item => item.id === cancelled.id ? cancelled : item));
      }
    });
  }

  renderContent(): void {
    if (!this.selectedContentIdForRender)
      return;
    this.service.createRenderJob(this.selectedContentIdForRender, { format: this.renderFormat }).subscribe({
      next: job => {
        this.renderJobs.set([job, ...this.renderJobs()]);
      }
    });
  }

  suggestPublishPlan(): void {
    if (!this.selectedContentIdForRender)
      return;
    const channelsJson = JSON.stringify(this.resolvePublishChannelsForSelectedContent());
    this.service.createPublishPlan(this.selectedContentIdForRender, {
      consentScope: 'marketing',
      channelsJson
    }).subscribe({
      next: plan => this.publishPlans.set([plan, ...this.publishPlans()])
    });
  }

  openArticlePublishing(brief: GrowthContentBrief): void {
    const linked = this.producedAssets().find(item => item.briefId === brief.id);
    if (linked) {
      this.router.navigate(['/growth/marketing/article-publishing', linked.id], {
        queryParams: { briefId: brief.id }
      });
      return;
    }
    this.router.navigate(['/growth/marketing/article-publishing']);
  }

  private resolvePublishChannelsForSelectedContent(): string[] {
    const content = this.producedAssets().find(item => item.id === this.selectedContentIdForRender);
    if (!content?.briefId) {
      return this.channelTargets.map(item => item.channelKey);
    }

    const brief = this.briefs().find(item => item.id === content.briefId);
    if (!brief) {
      return this.channelTargets.map(item => item.channelKey);
    }

    const targets = this.parseJson<BriefChannelTargetRow[]>(brief.channelTargetsJson, [{ channelKey: 'web-blog', priority: 1 }]);
    return targets.map(item => item.channelKey);
  }

  createInsight(): void {
    this.service.createInsight({
      scope: 'tenant',
      period: 'last-30-days',
      segmentId: this.insightSegmentId,
      citationRefsJson: JSON.stringify([{ source: 'engagement-summary' }])
    }).subscribe({
      next: insight => {
        this.latestInsightId = insight.id;
        this.insights.set([insight, ...this.insights()]);
      }
    });
  }

  createBriefFromInsight(): void {
    if (!this.latestInsightId)
      return;
    this.service.createBriefFromInsight(this.latestInsightId, {
      idempotencyKey: `brief-from-insight-${this.latestInsightId}-${Date.now()}`
    }).subscribe({
      next: brief => this.briefs.set([brief, ...this.briefs()])
    });
  }

  addChannelTarget(): void { this.channelTargets.push({ channelKey: 'web-blog', priority: 1 }); }
  removeChannelTarget(index: number): void { this.channelTargets.splice(index, 1); }
  addSourceBinding(): void { this.sourceBindings.push({ bindingType: 'catalog', bindingRef: '' }); }
  removeSourceBinding(index: number): void { this.sourceBindings.splice(index, 1); }

  private parseJson<T>(json: string, fallback: T): T {
    try { return JSON.parse(json) as T; } catch { return fallback; }
  }

  private resolveBriefStatusKey(status: string | number): string | null {
    const byName: Record<string, number> = {
      Draft: 0,
      Ready: 1,
      Producing: 2,
      Produced: 3,
      Cancelled: 4,
      Failed: 5
    };
    const normalized = typeof status === 'number'
      ? status
      : byName[status] ?? Number.parseInt(String(status), 10);
    const keys = [
      'growth.content.ace.briefStatus.draft',
      'growth.content.ace.briefStatus.ready',
      'growth.content.ace.briefStatus.producing',
      'growth.content.ace.briefStatus.produced',
      'growth.content.ace.briefStatus.cancelled',
      'growth.content.ace.briefStatus.failed'
    ];
    return Number.isInteger(normalized) && normalized >= 0 && normalized < keys.length
      ? keys[normalized]
      : null;
  }

  t(key: string): string {
    return this.i18n.translate(key);
  }
}
